/**
 * Orchestra AI Platform - Enhanced API Toolbox Component
 * 
 * Comprehensive toolbox for discovering, testing, and managing API components
 * 
 * @fileoverview Enhanced API toolbox with search, filters, categories, and saved components
 */

import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { APICard } from './APICard';
import { SearchBar } from './SearchBar';
import { useAPISearch } from '../../hooks/useAPISearch';
import { API_CATEGORIES, getCategoriesForServer } from '../../utils/apiCategories';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import './APIToolbox.css';

/**
 * @typedef {Object} APIToolboxProps
 * @property {Object[]} [apis] - Array of API definitions
 * @property {Function} [onAPISelect] - API selection handler
 */

/**
 * Enhanced API Toolbox Component
 * @param {APIToolboxProps} props
 */
export function APIToolbox({ apis = [], onAPISelect }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedServer, setSelectedServer] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const [collapsedServers, setCollapsedServers] = useState({});
  const [savedComponents, setSavedComponents] = useState([]);
  const [recentAPIs, setRecentAPIs] = useState([]);

  // Load saved components and recent APIs
  useEffect(() => {
    loadSavedComponents();
    loadRecentAPIs();
  }, []);

  // Use search hook
  const filteredAPIs = useAPISearch(apis, searchQuery, {
    server: selectedServer !== 'all' ? selectedServer : undefined,
    category: selectedCategory !== 'all' ? selectedCategory : undefined,
    onlyFavorites,
  });

  // Group APIs by server
  const apisByServer = useMemo(() => {
    const grouped = {
      chinese: [],
      english: [],
      maths: [],
      myclass: [],
    };

    filteredAPIs.forEach((api) => {
      if (grouped[api.server]) {
        grouped[api.server].push(api);
      }
    });

    return grouped;
  }, [filteredAPIs]);

  /**
   * Load saved components from database
   */
  const loadSavedComponents = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('developer_api_blocks')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSavedComponents(data || []);
    } catch (error) {
      console.error('Failed to load saved components:', error);
    }
  };

  /**
   * Load recent APIs from localStorage
   */
  const loadRecentAPIs = () => {
    try {
      const saved = localStorage.getItem('recent_apis');
      if (saved) {
        setRecentAPIs(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Failed to load recent APIs:', error);
    }
  };

  /**
   * Toggle server collapse
   * @param {string} server
   */
  const toggleServer = (server) => {
    setCollapsedServers((prev) => ({
      ...prev,
      [server]: !prev[server],
    }));
  };

  /**
   * Handle API favorite toggle
   * @param {Object} api
   */
  const handleFavorite = async (api) => {
    try {
      // Toggle favorite in localStorage (or database)
      const favorites = JSON.parse(localStorage.getItem('favorite_apis') || '[]');
      const index = favorites.indexOf(api.id);
      
      if (index > -1) {
        favorites.splice(index, 1);
      } else {
        favorites.push(api.id);
      }
      
      localStorage.setItem('favorite_apis', JSON.stringify(favorites));
      
      // Update API in list
      api.is_favorite = !api.is_favorite;
      toast.success(api.is_favorite ? 'Added to favorites' : 'Removed from favorites');
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  /**
   * Handle API test
   * @param {Object} api
   */
  const handleTest = (api) => {
    if (onAPISelect) {
      onAPISelect(api, 'test');
    }
  };

  /**
   * Handle API info
   * @param {Object} api
   */
  const handleInfo = (api) => {
    if (onAPISelect) {
      onAPISelect(api, 'info');
    }
  };

  /**
   * Render server section
   * @param {string} server
   * @param {Object[]} serverAPIs
   */
  const renderServerSection = (server, serverAPIs) => {
    if (serverAPIs.length === 0) return null;

    const isCollapsed = collapsedServers[server];
    const categories = getCategoriesForServer(server);
    const categoriesByAPI = {};

    // Group by category
    serverAPIs.forEach((api) => {
      if (!categoriesByAPI[api.category]) {
        categoriesByAPI[api.category] = [];
      }
      categoriesByAPI[api.category].push(api);
    });

    const serverLabels = {
      chinese: 'Chinese AI Server',
      english: 'English AI Server',
      maths: 'Math AI Server',
      myclass: 'MyClass AI Server',
    };

    return (
      <div key={server} className="server-section">
        <button
          onClick={() => toggleServer(server)}
          className="server-toggle-button"
        >
          <div className="server-toggle-content">
            <span className="server-toggle-icon">{isCollapsed ? '▶' : '▼'}</span>
            <h3 className="server-toggle-title">{serverLabels[server]}</h3>
            <span className="server-toggle-count">({serverAPIs.length})</span>
          </div>
        </button>

        {!isCollapsed && (
          <div className="server-content">
            {Object.entries(categoriesByAPI).map(([category, categoryAPIs]) => {
              const categoryInfo = categories.find((c) => c.id === category);
              return (
                <div key={category} className="category-section">
                  {categoryInfo && (
                    <h4 className="category-title">
                      {categoryInfo.name} ({categoryAPIs.length})
                    </h4>
                  )}
                  <div className="category-apis">
                    {categoryAPIs.map((api) => (
                      <APICard
                        key={api.id}
                        data={api}
                        onFavorite={() => handleFavorite(api)}
                        onTest={() => handleTest(api)}
                        onInfo={() => handleInfo(api)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="api-toolbox">
      {/* Header */}
      <div className="toolbox-header">
        <h1>API Toolbox</h1>
        <div className="toolbox-filters">
          <div className="search-container">
            <SearchBar value={searchQuery} onChange={setSearchQuery} />
          </div>
          <select
            value={selectedServer}
            onChange={(e) => setSelectedServer(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Servers</option>
            <option value="chinese">Chinese AI</option>
            <option value="english">English AI</option>
            <option value="maths">Math AI</option>
            <option value="myclass">MyClass AI</option>
          </select>
        </div>
        <div className="api-count">
          {filteredAPIs.length} of {apis.length} APIs
        </div>
      </div>

      {/* Content */}
      <div className="toolbox-content">
        {filteredAPIs.length === 0 ? (
          <div className="empty-state">
            <p>No APIs found</p>
            <p className="hint">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div>
            {/* Recent APIs */}
            {recentAPIs.length > 0 && searchQuery === '' && (
              <div className="recent-apis-section">
                <h3 className="section-title">Recent APIs</h3>
                <div className="section-content">
                  {recentAPIs.slice(0, 5).map((apiId) => {
                    const api = apis.find((a) => a.id === apiId);
                    return api ? (
                      <APICard
                        key={api.id}
                        data={api}
                        onFavorite={() => handleFavorite(api)}
                        onTest={() => handleTest(api)}
                        onInfo={() => handleInfo(api)}
                      />
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {/* APIs by Server */}
            {Object.entries(apisByServer).map(([server, serverAPIs]) =>
              renderServerSection(server, serverAPIs)
            )}

            {/* Saved Components */}
            {savedComponents.length > 0 && (
              <div className="recent-apis-section">
                <h3 className="section-title">My Components</h3>
                <div className="section-content">
                  {savedComponents.map((component) => (
                    <div
                      key={component.id}
                      style={{
                        padding: '0.75rem',
                        backgroundColor: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        borderRadius: '0.5rem'
                      }}
                    >
                      <h4 style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                        {component.name}
                      </h4>
                      <p style={{ fontSize: '0.75rem', color: '#4b5563', marginTop: '0.25rem' }}>
                        {component.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="toolbox-footer">
        {filteredAPIs.length} of {apis.length} APIs
      </div>
    </div>
  );
}

APIToolbox.propTypes = {
  apis: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      server: PropTypes.string.isRequired,
      endpoint: PropTypes.string.isRequired,
      method: PropTypes.string.isRequired,
      category: PropTypes.string.isRequired,
    })
  ),
  onAPISelect: PropTypes.func,
};

